import React, { useState } from 'react';

import { Select as MUISelect } from '@mui/material';
import type { SelectClasses } from '@mui/material';
import clsx from 'clsx';
import { useField } from 'formik';
import { DownArrowIconSize12 } from 'src/components/icons/DownArrowIconSize12';
import { makeStyles } from 'tss-react/mui';

import { useInputStyles } from './useInputStyles';

const useStyles = makeStyles()(theme => ({
  inputIcon: {
    marginRight: theme.spacing(1),
    marginTop: 6,
    transform: 'none !important', // Prevent rotation that MUI does on open
  },
  inputText: {
    padding: theme.spacing(0, 1),
  },
}));

interface FormikBaseSelectProps {
  children: React.ReactNode;
  fieldName: string;
  /**
   * This function is used to render the inside of the container of the select
   * component. This is where the value is displayed.
   */
  renderValue: (value: unknown) => JSX.Element;
  className?: string;
  disabled?: boolean;
  selectClasses?: Partial<SelectClasses>;
}

/**
 * This is the base select component that can be used to create custom select
 * components like FormikOptionsSelect, FormikBorderSelect and
 * FormikColorSelect.
 *
 * Primarily customized through:
 *
 * - `renderValue`: This function is used to render the inside of the input box
 * - `children`: The options that will be displayed in the select component
 */
export function FormikBaseSelect<T extends string>(props: FormikBaseSelectProps): JSX.Element {
  const { children, className, disabled, fieldName, renderValue, selectClasses } = props;
  props;
  const [isOpen, setIsOpen] = useState(false);
  const { classes } = useStyles();
  const { classes: inputClasses } = useInputStyles({ disabled, isOpen });

  const [field] = useField<T | undefined>(fieldName);

  return (
    <MUISelect
      IconComponent={({ className, ...props }) => (
        <DownArrowIconSize12 className={clsx(className, classes.inputIcon)} {...props} />
      )}
      MenuProps={{
        anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        transformOrigin: { horizontal: 'left', vertical: 'top' },
        variant: 'menu',
      }}
      className={clsx(inputClasses.inputBox, className)}
      classes={selectClasses}
      disableUnderline={true}
      displayEmpty={true}
      renderValue={renderValue}
      value={field.value}
      onClose={() => setIsOpen(false)}
      onOpen={() => setIsOpen(true)}
    >
      {children}
    </MUISelect>
  );
}
